// 访问记录器：所有访问/切 region 信息先在内存里累计，再统一上报给后端
// - 首次（首条 visit）走 /api/log/visit 落库
// - 后续全部由心跳（/api/log/heartbeat）带 regionCodes 数组一并更新同一行
// - 不再为「切 region」单独发接口，避免访问记录一秒多行。
//
// ★ 2026-10-28 访客标识机制
//   未登录访客没有 session_token，此前刷新页面就会多出一行记录（PV 虚高）。
//   现生成一个持久化在 localStorage 的 visitorId 随请求带上，
//   后端据此把同一访客的多次访问合并为一行；登录后由 session_token 接管。
//   visitorId 仅作去重键，不含任何个人信息。

import { http } from '@/api/request';

interface Tracker {
  firstAt: string;                // 首次访问时间（ISO）
  path: string;                   // 当前最新 path
  regionCodes: string[];          // 本会话累计去重的 region 列表
  inserted: boolean;              // 首条 visit 是否已落库
}

const t: Tracker = {
  firstAt: '',
  path: '',
  regionCodes: [],
  inserted: false
};

const VISITOR_KEY = 'nrap_vid';

/**
 * 访客标识：首次访问时生成一个随机 id 存入 localStorage，长期保持不变。
 * - localStorage 不可用（隐私模式 / 跨域限制）时退回 sessionStorage
 * - 都没有时返回空串，后端会回退用 IP+UA 派生兜底
 */
function getVisitorId(): string {
  try {
    let v = localStorage.getItem(VISITOR_KEY);
    if (!v) {
      v = 'v_' + (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36));
      localStorage.setItem(VISITOR_KEY, v);
    }
    return v;
  } catch {
    try {
      let v = sessionStorage.getItem(VISITOR_KEY);
      if (!v) {
        v = 's_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
        sessionStorage.setItem(VISITOR_KEY, v);
      }
      return v;
    } catch {
      return '';
    }
  }
}

/** 访客标识在页面内缓存，避免每次请求都读存储 */
let visitorId = '';
function visitor(): string {
  if (!visitorId) visitorId = getVisitorId();
  return visitorId;
}

function uniqPush(arr: string[], v: string) {
  if (v && !arr.includes(v)) arr.push(v);
}

function currentPath(): string {
  return location.pathname + location.search;
}

/** 记录一次访问/切换 region：仅更新内存，调用方不需关心是否需要发请求 */
export function recordVisit(opts?: { path?: string; regionCode?: string }) {
  const path = opts?.path ?? currentPath();
  const rc = opts?.regionCode;
  t.path = path;
  if (!t.firstAt) t.firstAt = new Date().toISOString();
  if (rc) uniqPush(t.regionCodes, rc);
  if (!t.inserted) flush();
}

/** 把内存里的累计信息发到后端（首次走 visit 接口 INSERT，后续走 heartbeat UPSERT） */
export async function flush() {
  if (!t.path) return;
  if (!t.inserted) {
    t.inserted = true;
    try {
      await http.post<{ ok: true }>('/log/visit', {
        path: t.path,
        regionCode: t.regionCodes[t.regionCodes.length - 1],
        regionCodes: t.regionCodes,
        visitorId: visitor()
      });
    } catch {
      /* ★ 2026-10-27：首访落库失败（如后端瞬时 502）时回滚 inserted，
         允许下次重试 visit。原先一旦失败就永久转入 heartbeat 分支，
         而未登录用户的 heartbeat 会被后端静默忽略 → 该访客访问记录永久丢失。 */
      t.inserted = false;
    }
  } else {
    try {
      await http.post<{ ok: true }>('/log/heartbeat', {
        regionCode: t.regionCodes[t.regionCodes.length - 1] || null,
        regionCodes: t.regionCodes,
        path: t.path,
        visitorId: visitor()
      });
    } catch { /* 静默 */ }
  }
}

/** 心跳 tick 调用：仅把累计信息同步给后端 */
export async function flushHeartbeat() {
  if (!t.inserted) { await flush(); return; }
  await flush();
}
